import React, { useCallback } from "react";
import { useParams } from "react-router-dom";
import { Typography, Container, Box, Paper } from "@mui/material";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { SkuTableSection } from "./SkuTableSection";
import { ShiftSchedulesSection } from "./ShiftSchedulesSection";
import { SightUsersSection } from "./SightUsersSection";
import { AlertRulesSection } from "./AlertRulesSection";
import { AdlRulesSection } from "./AdlRulesSection";
import { SenseConfigsSection } from "./SenseConfigsSection";
import { ConfigurationTabs } from "./ConfigurationTabs";

/**
 * Configuration page component that renders different configuration sections
 * based on the configType URL parameter
 */

export const ConfigurationPage: React.FC = () => {
  const { configType } = useParams<{ configType: string }>();
  const ckey = useCustomerKey();
  const { translate } = useFlightTranslation();
  // Function to render the appropriate configuration section based on configType
  const renderConfigSection = useCallback(() => {
    switch (configType) {
      case "skuTable":
        return <SkuTableSection />;
      case "users":
        return <SightUsersSection />;
      case "alertRules":
        return <AlertRulesSection />;
      case "shiftSchedules":
        return <ShiftSchedulesSection />;
      case "adlRules":
        return <AdlRulesSection />;
      case "sense":
        return <SenseConfigsSection />;
      default:
        return (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5">{translate("Configuration")}</Typography>
            <Typography variant="body1" color="error" sx={{ mt: 2 }}>
              {translate("Unknown configuration type")}: {configType}
            </Typography>
          </Paper>
        );
    }
  }, [configType, translate]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4">{translate("Configuration")}</Typography>
        <Typography variant="subtitle1">
          {translate("Customer")}: {ckey}
        </Typography>
      </Box>
      <ConfigurationTabs />
      {renderConfigSection()}
    </Container>
  );
};

export default ConfigurationPage;
